import { useAppStateDispatch, useAppStateSelector } from '@src/providers/AppStateProvider';
import {
  Box,
  Flex,
  FormGroup,
  getCustomerUserApiKeyDisplayDescription,
  getCustomerUserApiKeyDisplayLabel,
  SearchSelect,
  Text,
} from '@talos/kyoko';
import { useCallback, useEffect, useMemo } from 'react';
import { selectApiKey } from '../state/apiKeys/apiKeysSlice';
import { getActiveCustomerAccounts } from '../state/permissions/permissionsSlice';
import { ApiKeyAccountFiltersFormTable } from './ApiKeyAccountFiltersFormTable';
import { ApiKeyMarketAccountsDropdown } from './ApiKeyMarketAccountsDropdown';
import { ALL_PERMISSIONS, objectFiltersToAccountPermissions, type ObjectFilterPermissionType } from './objectFilters';
import { useCustomerApiKeys } from './useCustomerApiKeys';

export function ApiKeyAccountFiltersTab() {
  const dispatch = useAppStateDispatch();
  const { apiKeys, deletedApiKeys, fetchApiKeys, selectedApiKeyId } = useCustomerApiKeys();
  const customerUserId = useAppStateSelector(state => state.customerUserDrawer.userDetails.customerUserId);
  const { customerAccounts, deletedCustomerAccounts } = useAppStateSelector(
    state => state.customerUserDrawer.permissions
  );

  useEffect(() => {
    if (customerUserId) {
      fetchApiKeys(customerUserId);
    }
  }, [customerUserId, fetchApiKeys]);

  const selectableKeys = useMemo(
    () => (apiKeys ?? []).filter(key => !deletedApiKeys.includes(key.CustomerUserApiKeyID)),
    [apiKeys, deletedApiKeys]
  );

  // The slice keeps selectedApiKeyId in sync as keys are loaded, added and deleted; fall back to the
  // first key for the render that follows a change it hasn't observed yet.
  const selectedApiKey =
    selectableKeys.find(key => key.CustomerUserApiKeyID === selectedApiKeyId) ?? selectableKeys.at(0);

  /** Actions the customer user is authorized for on each of their accounts. Empty when the user is unrestricted. */
  const authorizedPermissionsByAccount = useMemo(
    () =>
      new Map(
        getActiveCustomerAccounts(customerAccounts, deletedCustomerAccounts).map(account => [
          account.customerAccount,
          account.permissions,
        ])
      ),
    [customerAccounts, deletedCustomerAccounts]
  );

  // An unrestricted user can assign any action; a restricted one can only assign what they are authorized for.
  const getAllowedPermissions = useCallback(
    (customerAccount: string): ObjectFilterPermissionType[] =>
      authorizedPermissionsByAccount.size === 0
        ? ALL_PERMISSIONS
        : (authorizedPermissionsByAccount.get(customerAccount) ?? []),
    [authorizedPermissionsByAccount]
  );

  const assignedAccounts = useMemo(
    () => objectFiltersToAccountPermissions(selectedApiKey?.ObjectFilters).map(account => account.customerAccount),
    [selectedApiKey?.ObjectFilters]
  );

  if (!selectedApiKey) {
    return (
      <Box pt="spacing16" data-testid="api-key-account-filters">
        <Text color="colorTextMuted">Generate an API key on the API Keys tab before configuring account filters.</Text>
      </Box>
    );
  }

  return (
    <Flex flexDirection="column" gap="spacing12" h="100%" pt="spacing16" data-testid="api-key-account-filters">
      <FormGroup label="API Key" mb="0">
        <SearchSelect
          data-testid="api-key-selector"
          options={selectableKeys}
          getLabel={getCustomerUserApiKeyDisplayLabel}
          getDescription={getCustomerUserApiKeyDisplayDescription}
          showDescriptionInButton
          selection={selectedApiKey}
          onChange={key => key && dispatch(selectApiKey(key.CustomerUserApiKeyID))}
        />
      </FormGroup>

      <Flex flexDirection="column" gap="spacing16" flex={1} minHeight={0}>
        <Flex flexDirection="column" gap="spacing8">
          <Text color="colorTextMuted">
            {assignedAccounts.length === 0
              ? 'Unrestricted — this key can access all accounts.'
              : 'This key is limited to the accounts below.'}
          </Text>
          <Text>Selected accounts</Text>
          <ApiKeyMarketAccountsDropdown
            apiKeyId={selectedApiKey.CustomerUserApiKeyID}
            assignedAccounts={assignedAccounts}
          />
        </Flex>
        <ApiKeyAccountFiltersFormTable
          apiKeyId={selectedApiKey.CustomerUserApiKeyID}
          getAllowedPermissions={getAllowedPermissions}
        />
      </Flex>
    </Flex>
  );
}
